// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: ClickUp tasks store — my open tasks, per-task detail (comments +
// statuses), time tracking (timer + manual entries), and mutations that keep
// the list in sync.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import Activity from '@app/features/konvo_activity/state/Activity';
import {locationIdsForTask} from '@app/features/konvo_tasks/utils/TaskLocations';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {RETRY_RATE_LIMITS} from '@app/features/platform/transport/RestRetry';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {VisiblePoll} from '@app/features/ui/utils/VisiblePoll';
import UserSettings from '@app/features/user/state/UserSettings';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('KonvoTasks');

function toTimeTrackingError(error: unknown): Error {
	if (error instanceof KonvoTimeTrackingError) return error;
	const body = (error as {body?: {error?: string}} | undefined)?.body;
	return new KonvoTimeTrackingError(typeof body?.error === 'string' ? body.error : 'failed');
}

/** Parses "1h 30m", "90m", "1.5h", "1:30" or a bare number of minutes into milliseconds. */
export function parseDurationInput(raw: string): number | null {
	const text = raw.trim().toLowerCase();
	if (!text) return null;
	const clock = /^(\d{1,2}):(\d{2})$/.exec(text);
	if (clock) return (Number(clock[1]) * 60 + Number(clock[2])) * 60_000;
	if (/^\d+(\.\d+)?$/.test(text)) return Number(text) * 60_000;
	let total = 0;
	let matched = false;
	const pattern = /(\d+(?:\.\d+)?)\s*(h|hr|hrs|hour|hours|m|min|mins|minute|minutes|d|day|days)\b/g;
	for (const match of text.matchAll(pattern)) {
		matched = true;
		const value = Number(match[1]);
		const unit = match[2]!;
		if (unit.startsWith('h')) total += value * 3_600_000;
		else if (unit.startsWith('d')) total += value * 8 * 3_600_000;
		else total += value * 60_000;
	}
	if (!matched || total <= 0) return null;
	return Math.round(total);
}

export function formatDuration(ms: number | null | undefined): string {
	if (ms == null || ms <= 0) return '0m';
	const minutes = Math.round(ms / 60_000);
	const hours = Math.floor(minutes / 60);
	const rest = minutes % 60;
	if (hours === 0) return `${rest}m`;
	return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

export function formatClock(ms: number): string {
	const total = Math.max(0, Math.floor(ms / 1000));
	const h = Math.floor(total / 3600);
	const m = Math.floor((total % 3600) / 60);
	const s = total % 60;
	const pad = (n: number) => String(n).padStart(2, '0');
	return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const TIMER_POLL_INTERVAL_MS = 60 * 1000;
/** A running-timer answer this fresh is reused by the next asker. */
const RUNNING_TIMER_REUSE_MS = 5 * 1000;
const PAUSED_TIMER_STORAGE_KEY = 'konvo:tasks:paused-timer';

export interface KonvoPausedTimer {
	taskId: string;
	taskName: string;
	/** Milliseconds tracked in the chunk that was paused, for the sidebar readout. */
	elapsedMs: number;
	pausedAt: number;
}

function readPausedTimer(): KonvoPausedTimer | null {
	try {
		const raw = getProtectedLocalStorage()?.getItem(PAUSED_TIMER_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<KonvoPausedTimer>;
		if (typeof parsed.taskId !== 'string' || !parsed.taskId) return null;
		return {
			taskId: parsed.taskId,
			taskName: typeof parsed.taskName === 'string' ? parsed.taskName : '',
			elapsedMs: typeof parsed.elapsedMs === 'number' ? parsed.elapsedMs : 0,
			pausedAt: typeof parsed.pausedAt === 'number' ? parsed.pausedAt : Date.now(),
		};
	} catch {
		return null;
	}
}

function writePausedTimer(value: KonvoPausedTimer | null): void {
	try {
		if (value) getProtectedLocalStorage()?.setItem(PAUSED_TIMER_STORAGE_KEY, JSON.stringify(value));
		else getProtectedLocalStorage()?.removeItem(PAUSED_TIMER_STORAGE_KEY);
	} catch {
		// storage unavailable; the pause is still honoured for this session
	}
}

export interface KonvoTaskUser {
	id: number | null;
	username: string | null;
	email: string | null;
	initials: string | null;
	color: string | null;
	profile_picture: string | null;
}
export interface KonvoTaskStatus {
	status: string | null;
	color: string | null;
	type: string | null;
}
export interface KonvoTaskListStatus extends KonvoTaskStatus {
	orderindex: number;
}
export interface KonvoSubtask {
	id: string | null;
	name: string;
	status: KonvoTaskStatus;
	assignees: Array<KonvoTaskUser>;
	due_date: number | null;
}
export interface KonvoTaskAttachment {
	id: string | null;
	title: string;
	url: string | null;
	thumbnail: string | null;
	/** A picture's full size through the media proxy, for the media viewer; absent for other files. */
	preview?: string | null;
	mimetype: string | null;
	size: number | null;
	date: number | null;
}
export interface KonvoTask {
	id: string | null;
	custom_id: string | null;
	name: string;
	description: string;
	/** The web pictures the description names (as written → media proxy address); task detail only. */
	description_images?: Record<string, string>;
	status: KonvoTaskStatus;
	priority: {id: number | null; name: string | null; color: string | null} | null;
	due_date: number | null;
	due_date_time: boolean;
	start_date: number | null;
	date_created: number | null;
	date_updated: number | null;
	time_estimate: number | null;
	time_spent: number | null;
	attachments?: Array<KonvoTaskAttachment>;
	url: string | null;
	creator: KonvoTaskUser | null;
	assignees: Array<KonvoTaskUser>;
	tags: Array<{name: string | null; color: string | null}>;
	list: {id: string | null; name: string | null};
	folder: {id: string | null; name: string | null};
	space: {id: string | null; name: string | null};
	parent: string | null;
	subtasks: Array<KonvoSubtask>;
	/** From the workspace task index; absent on older payloads. */
	date_closed?: number | null;
	date_done?: number | null;
	subtask_total?: number;
	subtask_done?: number;
	comment_count?: number | null;
	/** ClickUp custom fields on the task's list, with or without a value. */
	custom_fields?: Array<KonvoTaskField>;
}
export interface KonvoFieldOption {
	id: string;
	name: string;
	color: string | null;
}
/**
 * A custom field on a task. `value` by type: the option id (drop_down), option
 * ids (labels), user ids (users), names (tasks, list_relationship), a number
 * (number, currency, emoji rating, progress percent, attachment count), ms
 * (date), a boolean (checkbox), otherwise text; null when empty.
 */
export interface KonvoTaskField {
	id: string;
	name: string;
	type: string;
	value: string | number | boolean | Array<string> | null;
	options?: Array<KonvoFieldOption>;
	users?: Array<KonvoTaskUser>;
	currency?: string | null;
	max?: number | null;
}
/** A list's custom field with every option, for editors. */
export interface KonvoFieldDefinition {
	id: string;
	name: string;
	type: string;
	options: Array<KonvoFieldOption>;
	currency: string | null;
	max: number | null;
	required: boolean;
}
export interface KonvoFieldEdit {
	id: string;
	/** null clears the field. */
	value: string | number | boolean | Array<string> | null;
	/** Date fields: whether the time of day counts. */
	time?: boolean;
}
export interface KonvoTaskComment {
	id: string | null;
	text: string;
	user: KonvoTaskUser | null;
	date: number | null;
	resolved: boolean;
	assignee: KonvoTaskUser | null;
}
export interface KonvoTaskDetail {
	task: KonvoTask;
	comments: Array<KonvoTaskComment>;
	statuses: Array<KonvoTaskListStatus>;
	clickup_user_id: string | null;
	own_token: boolean;
}
export interface KonvoTimeEntry {
	id: string | null;
	task: {id: string | null; name: string | null; custom_id: string | null};
	user: KonvoTaskUser | null;
	start: number | null;
	end: number | null;
	/** Milliseconds; null while the entry is still running. */
	duration: number | null;
	running: boolean;
	description: string;
	billable: boolean;
}
export interface KonvoTaskTime {
	entries: Array<KonvoTimeEntry>;
	total_ms: number;
	clickup_user_id: string | null;
	own_token: boolean;
}
export class KonvoTimeTrackingError extends Error {
	constructor(readonly code: string) {
		super(code);
	}
}
export interface KonvoTaskPatch {
	name?: string;
	description?: string;
	status?: string;
	priority?: number | null;
	due_date_ms?: number | null;
	due_date_time?: boolean;
	assignees?: {add?: Array<number>; rem?: Array<number>};
	start_date_ms?: number | null;
	time_estimate_ms?: number | null;
	tags?: {add?: Array<string>; rem?: Array<string>};
	/** Moves the task to another list. */
	list_id?: string;
	custom_fields?: Array<KonvoFieldEdit>;
}

export {locationIdsForTask};
export type KonvoTaskGroupKey = 'overdue' | 'today' | 'week' | 'later' | 'none';
export type KonvoTaskGroupBy = 'due' | 'location' | 'none';
export type KonvoTaskView = 'list' | 'board';
export type KonvoTaskScope = 'mine' | 'all';
export interface KonvoTaskGroup {
	key: string;
	kind: KonvoTaskGroupBy;
	/** Due-date bucket (kind 'due') or breadcrumb parts (kind 'location'). */
	dueKey: KonvoTaskGroupKey | null;
	path: Array<string>;
	tasks: Array<KonvoTask>;
	/** Tasks split by status, in workflow order. Empty unless grouping by location. */
	sections: Array<KonvoTaskStatusSection>;
}

/** One status heading inside a location group. */
export interface KonvoTaskStatusSection {
	key: string;
	label: string;
	color: string | null;
	type: string | null;
	tasks: Array<KonvoTask>;
}

/** One entry in the location nav: a space, a folder inside it, or a list. */
export interface KonvoTaskLocationNode {
	id: string;
	name: string;
	kind: 'space' | 'folder' | 'list';
	count: number;
	children: Array<KonvoTaskLocationNode>;
}
const GROUP_BY_STORAGE_KEY = 'konvo:tasks:group-by';
const INCLUDE_CLOSED_STORAGE_KEY = 'konvo:tasks:include-closed';
const VIEW_STORAGE_KEY = 'konvo:tasks:view';
const SCOPE_STORAGE_KEY = 'konvo:tasks:scope';
const FILTERS_STORAGE_KEY = 'konvo:tasks:filters';
const COLLAPSED_STORAGE_KEY = 'konvo:tasks:collapsed';
export const UNASSIGNED_KEY = '__unassigned__';

function readStoredSet(key: string): Set<string> {
	try {
		const raw = globalThis.localStorage?.getItem(key);
		const parsed = raw ? (JSON.parse(raw) as unknown) : null;
		return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
	} catch {
		return new Set();
	}
}

function writeStoredSet(key: string, value: Set<string>): void {
	try {
		globalThis.localStorage?.setItem(key, JSON.stringify([...value]));
	} catch {
		// storage unavailable
	}
}

/** One kanban column: a status name shared across lists. */
export interface KonvoTaskStatusColumn {
	key: string;
	label: string;
	color: string | null;
	type: string | null;
	count: number;
}
/** One kanban swimlane: a group (due bucket / location) split per status column. */
export interface KonvoTaskLane extends KonvoTaskGroup {
	cells: Map<string, Array<KonvoTask>>;
}

function readStoredString(key: string): string | null {
	try {
		return globalThis.localStorage?.getItem(key) ?? null;
	} catch {
		return null;
	}
}

function writeStoredString(key: string, value: string): void {
	try {
		globalThis.localStorage?.setItem(key, value);
	} catch {
		// storage unavailable
	}
}

/** ClickUp status types in workflow order; unknown types land with "custom". */
const STATUS_TYPE_RANK: Record<string, number> = {open: 0, unstarted: 1, custom: 2, active: 2, done: 3, closed: 4};
/** "to be assigned" → "To Be Assigned". ClickUp stores statuses lowercase. */
export function statusLabel(status: string | null | undefined): string {
	if (!status) return '';
	return status
		.split(/(\s+|[-/])/)
		.map((part) => (/^[\s\-/]+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
		.join('');
}

export function statusKey(status: Pick<KonvoTaskStatus, 'status'>): string {
	return (status.status ?? '').toLowerCase();
}

export interface KonvoTaskFilterOption {
	key: string;
	label: string;
	color: string | null;
	count: number;
}

/** "Space / Folder / List" (folder omitted when hidden). */
export function taskLocationPath(task: Pick<KonvoTask, 'space' | 'folder' | 'list'>): Array<string> {
	return [task.space.name, task.folder.name, task.list.name].filter((part): part is string => Boolean(part));
}

export function isTaskClosed(task: Pick<KonvoTask, 'status'>): boolean {
	return task.status.type === 'closed' || task.status.type === 'done';
}

/** The list status "Mark complete" moves a task to: its first closed, then done, then the last. */
export function closedStatusOf(statuses: Array<KonvoTaskListStatus>): KonvoTaskListStatus | undefined {
	return (
		statuses.find((status) => status.type === 'closed') ??
		statuses.find((status) => status.type === 'done') ??
		statuses[statuses.length - 1]
	);
}

function startOfDay(d: Date): Date {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function groupKeyForTask(task: KonvoTask, now = new Date()): KonvoTaskGroupKey {
	if (task.due_date == null) return 'none';
	const due = new Date(task.due_date);
	const today = startOfDay(now).getTime();
	const tomorrow = today + 86_400_000;
	const weekEnd = today + 7 * 86_400_000;
	const dueDay = startOfDay(due).getTime();
	if (dueDay < today) return 'overdue';
	if (dueDay < tomorrow) return 'today';
	if (dueDay < weekEnd) return 'week';
	return 'later';
}

const taskChangeListeners = new Set<(task: KonvoTask) => void>();

class KonvoTasksStore {
	tasks: Array<KonvoTask> = [];
	connected: boolean | null = null;
	loading = false;
	fetched = false;
	error: string | null = null;
	includeClosed = false;
	query = '';
	// Locations is the primary task organization on both desktop and mobile.
	groupBy: KonvoTaskGroupBy = 'location';
	view: KonvoTaskView = 'list';
	scope: KonvoTaskScope = 'mine';
	hiddenStatuses: Set<string> = readStoredSet(`${FILTERS_STORAGE_KEY}:status`);
	hiddenAssignees: Set<string> = readStoredSet(`${FILTERS_STORAGE_KEY}:assignee`);
	hiddenSpaces: Set<string> = readStoredSet(`${FILTERS_STORAGE_KEY}:space`);
	collapsedGroups: Set<string> = readStoredSet(COLLAPSED_STORAGE_KEY);
	/** Selected entry in the location nav: a space/folder/list id, or null for all. */
	locationId: string | null = null;
	clickupUserId: string | null = null;
	details = observable.map<string, KonvoTaskDetail>();
	detailLoadingId: string | null = null;
	/** The task whose card is open, so the voice assistant knows what "this" is. */
	openTaskId: string | null = null;
	/** Time entries per task id, loaded when a task card opens. */
	time = observable.map<string, KonvoTaskTime>();
	/** The caller's running ClickUp timer, if any (null = none, undefined = not checked yet). */
	runningEntry: KonvoTimeEntry | null | undefined = undefined;
	/** Whether the caller can start/stop timers (needs their own linked ClickUp account). */
	timerAvailable: boolean | null = null;
	/** Today's entries + total, for the Today page. */
	timeToday: {entries: Array<KonvoTimeEntry>; total_ms: number; fetched_at: number} | null = null;
	/**
	 * ClickUp has no pause: "pause" stops the running entry and remembers the task so
	 * the sidebar can resume it as a new entry. Survives reloads via localStorage.
	 */
	pausedTimer: KonvoPausedTimer | null = readPausedTimer();
	private refreshTimer: VisiblePoll | null = null;
	private timerPoll: VisiblePoll | null = null;
	/** Bumped per request so a slow reply for an old scope never overwrites a newer one. */
	private refreshGeneration = 0;
	private runningTimerRequest: Promise<KonvoTimeEntry | null> | null = null;
	private runningTimerAt = 0;

	constructor() {
		makeAutoObservable<
			this,
			'refreshTimer' | 'timerPoll' | 'refreshGeneration' | 'runningTimerRequest' | 'runningTimerAt'
		>(
			this,
			{
				refreshGeneration: false,
				runningTimerRequest: false,
				runningTimerAt: false,
				refreshTimer: false,
				timerPoll: false,
				hiddenStatuses: observable,
				hiddenAssignees: observable,
				hiddenSpaces: observable,
				collapsedGroups: observable,
			},
			{autoBind: true},
		);
		if (readStoredString(INCLUDE_CLOSED_STORAGE_KEY) === 'true') this.includeClosed = true;
		this.hydrateLayout();
	}

	/**
	 * Layout lives in synced preferences so it follows the person between devices.
	 * Anything not synced yet falls back to the value this device stored locally,
	 * so an existing setup is not thrown away on first run.
	 */
	hydrateLayout(): void {
		const synced = UserSettings.getTasksView();
		const groupBy = synced?.groupBy || readStoredString(GROUP_BY_STORAGE_KEY);
		if (groupBy === 'due' || groupBy === 'location' || groupBy === 'none') this.groupBy = groupBy;
		const view = synced?.view || readStoredString(VIEW_STORAGE_KEY);
		if (view === 'list' || view === 'board') this.view = view;
		const scope = synced?.scope || readStoredString(SCOPE_STORAGE_KEY);
		if (scope === 'mine' || scope === 'all') this.scope = scope;
		this.locationId = synced?.locationId || null;
		if (synced?.collapsedGroupIds?.length) this.collapsedGroups = new Set(synced.collapsedGroupIds);
	}

	setGroupBy(value: KonvoTaskGroupBy): void {
		this.groupBy = value;
		writeStoredString(GROUP_BY_STORAGE_KEY, value);
		void UserSettings.setTasksView({groupBy: value});
	}

	setView(value: KonvoTaskView): void {
		this.view = value;
		writeStoredString(VIEW_STORAGE_KEY, value);
		void UserSettings.setTasksView({view: value});
	}

	setScope(value: KonvoTaskScope): void {
		if (this.scope === value) return;
		this.scope = value;
		writeStoredString(SCOPE_STORAGE_KEY, value);
		void UserSettings.setTasksView({scope: value});
		void this.refresh();
	}

	/** Location nav selection; null shows every location. */
	setLocationId(value: string | null): void {
		if (this.locationId === value) return;
		this.locationId = value;
		void UserSettings.setTasksView({locationId: value ?? ''});
	}

	/** Tasks assigned to me regardless of scope (badge, /done, Today page). */
	get myTasks(): Array<KonvoTask> {
		if (this.scope === 'mine') return this.tasks;
		const me = this.clickupUserId;
		if (!me) return [];
		return this.tasks.filter((task) => task.assignees.some((assignee) => String(assignee.id) === me));
	}

	prefetch(): void {
		if (!this.fetched && !this.loading) void this.refresh();
		if (this.refreshTimer == null) {
			this.refreshTimer = new VisiblePoll(REFRESH_INTERVAL_MS, () => {
				if (!this.loading) return this.refresh();
				return undefined;
			});
			this.refreshTimer.start();
		}
		// The running timer may be started or stopped from ClickUp itself, so keep the
		// sidebar chip honest with a light poll.
		if (this.timerPoll == null) {
			this.timerPoll = new VisiblePoll(TIMER_POLL_INTERVAL_MS, () => this.refreshRunningTimer(), true);
			this.timerPoll.start();
		}
	}

	setIncludeClosed(value: boolean): void {
		if (this.includeClosed === value) return;
		this.includeClosed = value;
		writeStoredString(INCLUDE_CLOSED_STORAGE_KEY, String(value));
		void this.refresh();
	}

	setQuery(value: string): void {
		this.query = value;
	}

	toggleHidden(kind: 'status' | 'assignee' | 'space', key: string): void {
		const set =
			kind === 'status' ? this.hiddenStatuses : kind === 'assignee' ? this.hiddenAssignees : this.hiddenSpaces;
		if (set.has(key)) set.delete(key);
		else set.add(key);
		writeStoredSet(`${FILTERS_STORAGE_KEY}:${kind}`, set);
	}

	clearFilters(): void {
		this.hiddenStatuses.clear();
		this.hiddenAssignees.clear();
		this.hiddenSpaces.clear();
		for (const kind of ['status', 'assignee', 'space']) writeStoredSet(`${FILTERS_STORAGE_KEY}:${kind}`, new Set());
	}

	get activeFilterCount(): number {
		return this.hiddenStatuses.size + this.hiddenAssignees.size + this.hiddenSpaces.size;
	}

	toggleGroupCollapsed(key: string): void {
		if (this.collapsedGroups.has(key)) this.collapsedGroups.delete(key);
		else this.collapsedGroups.add(key);
		writeStoredSet(COLLAPSED_STORAGE_KEY, this.collapsedGroups);
		void UserSettings.setTasksView({collapsedGroupIds: [...this.collapsedGroups]});
	}

	isGroupCollapsed(key: string): boolean {
		return this.collapsedGroups.has(key);
	}

	/** Top-level tasks (subtasks whose parent is also listed are folded into it). */
	get topLevelTasks(): Array<KonvoTask> {
		return this.tasks.filter((task) => task.parent == null || !this.tasks.some((t) => t.id === task.parent));
	}

	get statusOptions(): Array<KonvoTaskFilterOption> {
		const map = new Map<string, KonvoTaskFilterOption>();
		for (const task of this.topLevelTasks) {
			const key = (task.status.status ?? '').toLowerCase();
			if (!key) continue;
			const entry = map.get(key) ?? {key, label: task.status.status ?? key, color: task.status.color, count: 0};
			entry.count += 1;
			map.set(key, entry);
		}
		return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
	}

	get assigneeOptions(): Array<KonvoTaskFilterOption> {
		const map = new Map<string, KonvoTaskFilterOption>();
		for (const task of this.topLevelTasks) {
			if (task.assignees.length === 0) {
				const entry = map.get(UNASSIGNED_KEY) ?? {key: UNASSIGNED_KEY, label: 'Unassigned', color: null, count: 0};
				entry.count += 1;
				map.set(UNASSIGNED_KEY, entry);
			}
			for (const assignee of task.assignees) {
				if (assignee.id == null) continue;
				const key = String(assignee.id);
				const entry = map.get(key) ?? {
					key,
					label: assignee.username ?? assignee.email ?? key,
					color: assignee.color,
					count: 0,
				};
				entry.count += 1;
				map.set(key, entry);
			}
		}
		return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
	}

	get spaceOptions(): Array<KonvoTaskFilterOption> {
		const map = new Map<string, KonvoTaskFilterOption>();
		for (const task of this.topLevelTasks) {
			const key = task.space.id ?? '';
			if (!key) continue;
			const entry = map.get(key) ?? {key, label: task.space.name ?? 'Space', color: null, count: 0};
			entry.count += 1;
			map.set(key, entry);
		}
		return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
	}

	/**
	 * Loads the task list for the current scope. A change of scope or filter
	 * while a request is in flight starts a new request and the older reply is
	 * dropped, so the list never shows tasks for a scope the user has left.
	 */
	async refresh(): Promise<void> {
		const generation = ++this.refreshGeneration;
		this.loading = true;
		try {
			const response = await http.get<{
				connected: boolean;
				clickup_user_id?: string | null;
				tasks: Array<KonvoTask>;
				error?: string;
			}>(Endpoints.KONVO_CLICKUP_MY_TASKS, {
				query: {
					...(this.includeClosed ? {include_closed: 'true'} : {}),
					...(this.scope === 'all' ? {scope: 'all'} : {}),
				},
				...RETRY_RATE_LIMITS,
			});
			if (generation !== this.refreshGeneration) return;
			runInAction(() => {
				this.connected = response.body?.connected ?? false;
				this.clickupUserId = response.body?.clickup_user_id ?? null;
				this.tasks = response.body?.tasks ?? [];
				this.error = response.body?.error ?? null;
				this.fetched = true;
			});
		} catch (error) {
			if (generation !== this.refreshGeneration) return;
			logger.warn('Failed to load ClickUp tasks', error);
			runInAction(() => {
				this.error = 'load_failed';
				this.fetched = true;
			});
		} finally {
			if (generation === this.refreshGeneration) {
				runInAction(() => {
					this.loading = false;
				});
			}
		}
	}

	/** Everything the filters allow, ignoring the location nav (so nav counts stay stable). */
	get filteredTasksAnyLocation(): Array<KonvoTask> {
		const q = this.query.trim().toLowerCase();
		return this.topLevelTasks.filter((task) => {
			if (this.hiddenStatuses.has((task.status.status ?? '').toLowerCase())) return false;
			if (this.hiddenSpaces.size > 0 && task.space.id && this.hiddenSpaces.has(task.space.id)) return false;
			if (this.hiddenAssignees.size > 0) {
				if (task.assignees.length === 0) {
					if (this.hiddenAssignees.has(UNASSIGNED_KEY)) return false;
				} else if (
					task.assignees.every((assignee) => assignee.id != null && this.hiddenAssignees.has(String(assignee.id)))
				) {
					return false;
				}
			}
			if (!q) return true;
			return (
				task.name.toLowerCase().includes(q) ||
				(task.custom_id ?? '').toLowerCase().includes(q) ||
				(task.list.name ?? '').toLowerCase().includes(q) ||
				(task.space.name ?? '').toLowerCase().includes(q) ||
				(task.status.status ?? '').toLowerCase().includes(q)
			);
		});
	}

	get filteredTasks(): Array<KonvoTask> {
		const selected = this.locationId;
		if (!selected) return this.filteredTasksAnyLocation;
		return this.filteredTasksAnyLocation.filter((task) => locationIdsForTask(task).includes(selected));
	}

	/** Space → Folder → List, with a count on every node, built from the loaded tasks. */
	get locationTree(): Array<KonvoTaskLocationNode> {
		const spaces = new Map<string, KonvoTaskLocationNode>();
		const childOf = (parent: KonvoTaskLocationNode, id: string, name: string, kind: 'folder' | 'list') => {
			let node = parent.children.find((candidate) => candidate.id === id);
			if (!node) {
				node = {id, name, kind, count: 0, children: []};
				parent.children.push(node);
			}
			return node;
		};
		for (const task of this.filteredTasksAnyLocation) {
			const spaceId = task.space.id ? `space:${task.space.id}` : 'space:—';
			let space = spaces.get(spaceId);
			if (!space) {
				space = {id: spaceId, name: task.space.name ?? '—', kind: 'space', count: 0, children: []};
				spaces.set(spaceId, space);
			}
			space.count += 1;
			let parent = space;
			if (task.folder.id && task.folder.name) {
				parent = childOf(space, `folder:${task.folder.id}`, task.folder.name, 'folder');
				parent.count += 1;
			}
			if (task.list.id) {
				const list = childOf(parent, `list:${task.list.id}`, task.list.name ?? '—', 'list');
				list.count += 1;
			}
		}
		const byName = (a: KonvoTaskLocationNode, b: KonvoTaskLocationNode) => a.name.localeCompare(b.name);
		const sortDeep = (nodes: Array<KonvoTaskLocationNode>): Array<KonvoTaskLocationNode> =>
			nodes.sort(byName).map((node) => ({...node, children: sortDeep(node.children)}));
		return sortDeep([...spaces.values()]);
	}

	/** Split tasks into status headings, ordered by the board's status ranking. */
	statusSections(tasks: Array<KonvoTask>): Array<KonvoTaskStatusSection> {
		const order = new Map(this.statusColumns.map((column, index) => [column.key, index]));
		const map = new Map<string, KonvoTaskStatusSection>();
		for (const task of tasks) {
			const key = statusKey(task.status);
			let section = map.get(key);
			if (!section) {
				section = {
					key,
					label: statusLabel(task.status.status) || key,
					color: task.status.color,
					type: task.status.type,
					tasks: [],
				};
				map.set(key, section);
			}
			section.tasks.push(task);
		}
		return [...map.values()].sort((a, b) => (order.get(a.key) ?? 99) - (order.get(b.key) ?? 99));
	}

	get grouped(): Array<KonvoTaskGroup> {
		// Inside every group tasks run in workflow status order (open → … → closed),
		// then by due date, then by name, so the list reads like a board.
		const statusRank = new Map(this.statusColumns.map((column, index) => [column.key, index]));
		const byDue = (a: KonvoTask, b: KonvoTask) =>
			(statusRank.get(statusKey(a.status)) ?? 99) - (statusRank.get(statusKey(b.status)) ?? 99) ||
			(a.due_date ?? Infinity) - (b.due_date ?? Infinity) ||
			a.name.localeCompare(b.name);
		if (this.groupBy === 'none') {
			const tasks = [...this.filteredTasks].sort(byDue);
			return tasks.length
				? [{key: 'all', kind: 'none', dueKey: null, path: [], tasks, sections: this.statusSections(tasks)}]
				: [];
		}
		if (this.groupBy === 'location') {
			const buckets = new Map<string, KonvoTaskGroup>();
			for (const task of this.filteredTasks) {
				const path = taskLocationPath(task);
				const key = path.join(' / ') || '—';
				let group = buckets.get(key);
				if (!group) {
					group = {key, kind: 'location', dueKey: null, path: path.length ? path : ['—'], tasks: [], sections: []};
					buckets.set(key, group);
				}
				group.tasks.push(task);
			}
			return [...buckets.values()]
				.map((group) => {
					const tasks = group.tasks.sort(byDue);
					// Inside a location the list reads as status headings, which is the
					// shape people expect from a ClickUp list view.
					return {...group, tasks, sections: this.statusSections(tasks)};
				})
				.sort((a, b) => a.key.localeCompare(b.key));
		}
		const buckets: Record<KonvoTaskGroupKey, Array<KonvoTask>> = {
			overdue: [],
			today: [],
			week: [],
			later: [],
			none: [],
		};
		for (const task of this.filteredTasks) buckets[groupKeyForTask(task)].push(task);
		return (['overdue', 'today', 'week', 'later', 'none'] as const)
			.map((key) => {
				const tasks = buckets[key].sort(byDue);
				return {key, kind: 'due' as const, dueKey: key, path: [], tasks, sections: this.statusSections(tasks)};
			})
			.filter((group) => group.tasks.length > 0);
	}

	/** Kanban columns: every status seen on visible tasks, in workflow order. */
	get statusColumns(): Array<KonvoTaskStatusColumn> {
		const map = new Map<string, KonvoTaskStatusColumn>();
		for (const task of this.topLevelTasks) {
			const key = statusKey(task.status);
			if (!key || this.hiddenStatuses.has(key)) continue;
			const entry = map.get(key) ?? {
				key,
				label: statusLabel(task.status.status) || key,
				color: task.status.color,
				type: task.status.type,
				count: 0,
			};
			map.set(key, entry);
		}
		for (const task of this.filteredTasks) {
			const entry = map.get(statusKey(task.status));
			if (entry) entry.count += 1;
		}
		const rank = (column: KonvoTaskStatusColumn) => STATUS_TYPE_RANK[column.type ?? ''] ?? 2;
		return [...map.values()].sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
	}

	/** Kanban swimlanes: `grouped` with each group's tasks split per status column. */
	get board(): Array<KonvoTaskLane> {
		return this.grouped.map((group) => {
			const cells = new Map<string, Array<KonvoTask>>();
			for (const task of group.tasks) {
				const key = statusKey(task.status);
				const cell = cells.get(key);
				if (cell) cell.push(task);
				else cells.set(key, [task]);
			}
			return {...group, cells};
		});
	}

	/** Change status (kanban drop): applied optimistically, rolled back on failure. */
	async moveTask(taskId: string, column: KonvoTaskStatusColumn): Promise<void> {
		const index = this.tasks.findIndex((task) => task.id === taskId);
		if (index < 0) return;
		const previous = this.tasks[index]!.status;
		if (statusKey(previous) === column.key) return;
		this.tasks[index] = {
			...this.tasks[index]!,
			status: {status: column.label, color: column.color, type: column.type},
		};
		try {
			await this.updateTask(taskId, {status: column.label});
		} catch (error) {
			runInAction(() => {
				const current = this.tasks.findIndex((task) => task.id === taskId);
				if (current >= 0) this.tasks[current] = {...this.tasks[current]!, status: previous};
			});
			throw error;
		}
	}

	get dueSoonCount(): number {
		return this.myTasks.filter((task) => !isTaskClosed(task) && ['overdue', 'today'].includes(groupKeyForTask(task)))
			.length;
	}

	setOpenTask(taskId: string | null): void {
		this.openTaskId = taskId;
	}

	getTask(taskId: string): KonvoTask | null {
		return this.details.get(taskId)?.task ?? this.tasks.find((task) => task.id === taskId) ?? null;
	}

	async loadDetail(taskId: string, force = false): Promise<KonvoTaskDetail | null> {
		if (!force && this.details.has(taskId)) {
			void this.fetchDetail(taskId);
			return this.details.get(taskId) ?? null;
		}
		return await this.fetchDetail(taskId);
	}

	private async fetchDetail(taskId: string): Promise<KonvoTaskDetail | null> {
		this.detailLoadingId = taskId;
		try {
			// ClickUp's per-minute limit answers 429 with Retry-After (often right after the app reloads);
			// wait it out rather than showing "Could not load this task".
			const response = await http.get<KonvoTaskDetail>(Endpoints.KONVO_CLICKUP_TASK(taskId), {
				mode: 'auto-retry',
				retries: 3,
			});
			const detail = response.body;
			if (!detail?.task) return null;
			runInAction(() => {
				this.details.set(taskId, detail);
				this.mergeTask(detail.task);
			});
			return detail;
		} catch (error) {
			logger.warn('Failed to load ClickUp task', error);
			return null;
		} finally {
			runInAction(() => {
				if (this.detailLoadingId === taskId) this.detailLoadingId = null;
			});
		}
	}

	/** Workspace views listen here so an edit made anywhere shows up in every loaded view. */
	subscribeTaskChanges(listener: (task: KonvoTask) => void): () => void {
		taskChangeListeners.add(listener);
		return () => taskChangeListeners.delete(listener);
	}

	private mergeTask(task: KonvoTask): void {
		for (const listener of taskChangeListeners) listener(task);
		const index = this.tasks.findIndex((entry) => entry.id === task.id);
		if (index >= 0)
			this.tasks[index] = {
				...this.tasks[index],
				...task,
				subtasks: task.subtasks.length ? task.subtasks : this.tasks[index]!.subtasks,
			};
	}

	/** Put a task back in the list (e.g. reopened after a refresh already dropped it as closed). */
	upsertTask(task: KonvoTask): void {
		if (this.tasks.some((entry) => entry.id === task.id)) this.mergeTask(task);
		else {
			for (const listener of taskChangeListeners) listener(task);
			this.tasks.push(task);
		}
	}

	async updateTask(taskId: string, patch: KonvoTaskPatch): Promise<KonvoTask> {
		const response = await http.patch<{task: KonvoTask}>(Endpoints.KONVO_CLICKUP_TASK(taskId), {body: patch});
		const task = response.body?.task;
		if (!task) throw new Error('ClickUp returned an empty task');
		runInAction(() => {
			const detail = this.details.get(taskId);
			if (detail) this.details.set(taskId, {...detail, task: {...detail.task, ...task}});
			this.mergeTask(task);
		});
		return task;
	}

	async addComment(taskId: string, text: string): Promise<KonvoTaskComment> {
		const response = await http.post<{comment: KonvoTaskComment}>(Endpoints.KONVO_CLICKUP_TASK_COMMENTS(taskId), {
			body: {text},
		});
		const comment = response.body?.comment;
		if (!comment) throw new Error('ClickUp returned an empty comment');
		runInAction(() => {
			const detail = this.details.get(taskId);
			if (detail) this.details.set(taskId, {...detail, comments: [...detail.comments, comment]});
		});
		return comment;
	}

	/** Move a task to its list's first "closed"-type status. */
	async completeTask(taskId: string): Promise<KonvoTask> {
		const detail = (await this.loadDetail(taskId)) ?? (await this.fetchDetail(taskId));
		if (!detail) throw new Error('Task not found');
		const closed = closedStatusOf(detail.statuses);
		if (!closed?.status) throw new Error('This list has no closed status');
		return await this.updateTask(taskId, {status: closed.status});
	}

	/**
	 * Tick or untick a subtask from its parent's detail view. The parent's list
	 * shows the change at once and rolls back if ClickUp rejects it.
	 */
	async setSubtaskDone(parentId: string, subtaskId: string, done: boolean): Promise<void> {
		const parent = this.details.get(parentId);
		const statuses = parent?.statuses ?? [];
		const target = done ? closedStatusOf(statuses) : (statuses.find((status) => status.type === 'open') ?? statuses[0]);
		if (!target?.status) throw new Error('This list has no matching status');
		const next: KonvoTaskStatus = {status: target.status, color: target.color, type: target.type};
		const previous = parent?.task.subtasks.find((subtask) => subtask.id === subtaskId)?.status ?? null;
		const apply = (status: KonvoTaskStatus) => {
			const current = this.details.get(parentId);
			if (!current) return;
			const subtasks = current.task.subtasks.map((subtask) =>
				subtask.id === subtaskId ? {...subtask, status} : subtask,
			);
			this.details.set(parentId, {...current, task: {...current.task, subtasks}});
			this.mergeTask({...current.task, subtasks});
		};
		runInAction(() => apply(next));
		try {
			await http.patch<{task: KonvoTask}>(Endpoints.KONVO_CLICKUP_TASK(subtaskId), {body: {status: target.status}});
		} catch (error) {
			if (previous) runInAction(() => apply(previous));
			throw error;
		}
		runInAction(() => this.details.delete(subtaskId));
		void this.loadDetail(parentId, true);
	}

	/** Creates a subtask in the parent's list. */
	async createSubtask(parentId: string, name: string): Promise<void> {
		const parent = this.details.get(parentId)?.task ?? this.getTask(parentId);
		const listId = parent?.list.id;
		if (!listId) throw new Error('Parent task has no list');
		await http.post<{id: string; url: string}>(Endpoints.KONVO_CLICKUP_TASKS, {
			body: {list_id: listId, name, parent: parentId},
		});
		Activity.track('action.done', {action: 'task.create', origin: 'konvo'});
		await this.loadDetail(parentId, true);
	}

	/** Uploads a file as a ClickUp task attachment and returns it (with its URL). */
	async uploadAttachment(taskId: string, file: File): Promise<KonvoTaskAttachment> {
		const form = new FormData();
		form.append('file', file, file.name);
		const response = await http.post<{attachment: KonvoTaskAttachment}>(
			Endpoints.KONVO_CLICKUP_TASK_ATTACHMENTS(taskId),
			{
				body: form,
				timeoutMs: 120_000,
			},
		);
		const attachment = response.body?.attachment;
		if (!attachment) throw new Error('upload failed');
		runInAction(() => {
			const detail = this.details.get(taskId);
			if (detail) {
				const attachments = [...(detail.task.attachments ?? []), attachment];
				this.details.set(taskId, {...detail, task: {...detail.task, attachments}});
			}
		});
		return attachment;
	}

	// ── Time tracking ──────────────────────────────────────────────────────

	getTime(taskId: string): KonvoTaskTime | undefined {
		return this.time.get(taskId);
	}

	async loadTime(taskId: string): Promise<KonvoTaskTime | null> {
		try {
			const response = await http.get<KonvoTaskTime>(Endpoints.KONVO_CLICKUP_TASK_TIME(taskId));
			const time = response.body;
			if (!time) return null;
			runInAction(() => {
				this.time.set(taskId, time);
				this.timerAvailable = time.own_token;
			});
			return time;
		} catch (error) {
			logger.warn('Failed to load ClickUp time entries', error);
			return null;
		}
	}

	async loadTimeToday(): Promise<void> {
		try {
			const response = await http.get<{connected: boolean; entries: Array<KonvoTimeEntry>; total_ms: number}>(
				Endpoints.KONVO_CLICKUP_TIME_TODAY,
				{query: {tz: Intl.DateTimeFormat().resolvedOptions().timeZone}, mode: 'silent'},
			);
			const body = response.body;
			runInAction(() => {
				this.timeToday = body?.connected
					? {entries: body.entries ?? [], total_ms: body.total_ms ?? 0, fetched_at: Date.now()}
					: {entries: [], total_ms: 0, fetched_at: Date.now()};
			});
		} catch (error) {
			logger.warn("Failed to load today's time entries", error);
		}
	}

	/**
	 * The running timer. The sidebar chip, the Rail strip, task sheets and the minute poll all ask;
	 * at the same moment they share one request, and an answer under 5 s old is reused.
	 */
	refreshRunningTimer(): Promise<KonvoTimeEntry | null> {
		if (this.runningTimerRequest) return this.runningTimerRequest;
		if (this.runningEntry !== undefined && Date.now() - this.runningTimerAt < RUNNING_TIMER_REUSE_MS)
			return Promise.resolve(this.runningEntry);
		this.runningTimerRequest = this.fetchRunningTimer().finally(() => {
			this.runningTimerRequest = null;
		});
		return this.runningTimerRequest;
	}

	private async fetchRunningTimer(): Promise<KonvoTimeEntry | null> {
		try {
			const response = await http.get<{connected: boolean; own_token?: boolean; entry: KonvoTimeEntry | null}>(
				Endpoints.KONVO_CLICKUP_TIME_CURRENT,
			);
			const entry = response.body?.entry ?? null;
			this.runningTimerAt = Date.now();
			runInAction(() => {
				this.runningEntry = entry;
				if (response.body?.own_token != null) this.timerAvailable = response.body.own_token;
			});
			return entry;
		} catch (error) {
			logger.warn('Failed to load the running ClickUp timer', error);
			runInAction(() => {
				if (this.runningEntry === undefined) this.runningEntry = null;
			});
			return null;
		}
	}

	async startTimer(taskId: string, description = ''): Promise<KonvoTimeEntry> {
		try {
			const response = await http.post<{entry: KonvoTimeEntry | null}>(
				Endpoints.KONVO_CLICKUP_TASK_TIME_START(taskId),
				{
					body: description ? {description} : {},
				},
			);
			const entry = response.body?.entry;
			if (!entry) throw new KonvoTimeTrackingError('empty');
			runInAction(() => {
				this.runningEntry = entry;
				this.setPausedTimer(null);
			});
			void this.loadTime(taskId);
			return entry;
		} catch (error) {
			throw toTimeTrackingError(error);
		}
	}

	async stopTimer(): Promise<KonvoTimeEntry | null> {
		const previous = this.runningEntry ?? null;
		try {
			const response = await http.post<{entry: KonvoTimeEntry | null}>(Endpoints.KONVO_CLICKUP_TIME_STOP, {body: {}});
			if (response.body?.entry) Activity.track('action.done', {action: 'time.log', origin: 'konvo'});
			runInAction(() => {
				this.runningEntry = null;
				this.setPausedTimer(null);
			});
			const taskId = response.body?.entry?.task.id ?? previous?.task.id;
			if (taskId) {
				void this.loadTime(taskId);
				void this.loadDetail(taskId, true);
			}
			return response.body?.entry ?? null;
		} catch (error) {
			throw toTimeTrackingError(error);
		}
	}

	/** Stop the running entry but remember the task so it can be resumed from the sidebar. */
	async pauseTimer(): Promise<void> {
		const running = this.runningEntry;
		if (!running?.task.id) return;
		const paused: KonvoPausedTimer = {
			taskId: running.task.id,
			taskName: running.task.name ?? '',
			elapsedMs: running.start != null ? Math.max(0, Date.now() - running.start) : 0,
			pausedAt: Date.now(),
		};
		await this.stopTimer();
		runInAction(() => this.setPausedTimer(paused));
	}

	/** Start a fresh entry on the paused task. */
	async resumeTimer(): Promise<KonvoTimeEntry | null> {
		const paused = this.pausedTimer;
		if (!paused) return null;
		return await this.startTimer(paused.taskId);
	}

	discardPausedTimer(): void {
		this.setPausedTimer(null);
	}

	private setPausedTimer(value: KonvoPausedTimer | null): void {
		this.pausedTimer = value;
		writePausedTimer(value);
	}

	async logTime(taskId: string, durationMs: number, description = '', startMs?: number): Promise<KonvoTimeEntry> {
		try {
			const response = await http.post<{entry: KonvoTimeEntry | null}>(Endpoints.KONVO_CLICKUP_TASK_TIME(taskId), {
				body: {
					duration_ms: Math.round(durationMs),
					...(startMs != null ? {start_ms: Math.round(startMs)} : {}),
					...(description ? {description} : {}),
				},
			});
			const entry = response.body?.entry;
			if (!entry) throw new KonvoTimeTrackingError('empty');
			Activity.track('action.done', {action: 'time.log', origin: 'konvo'});
			await this.loadTime(taskId);
			void this.loadDetail(taskId, true);
			return entry;
		} catch (error) {
			throw toTimeTrackingError(error);
		}
	}

	async updateTimeEntry(
		taskId: string,
		entryId: string,
		durationMs: number,
		startMs: number,
		description: string,
	): Promise<void> {
		try {
			await http.patch(Endpoints.KONVO_CLICKUP_TIME_ENTRY(entryId), {
				body: {duration_ms: durationMs, start_ms: startMs, description},
			});
			await this.loadTime(taskId);
			await this.loadDetail(taskId, true);
			void this.loadTimeToday();
		} catch (error) {
			throw toTimeTrackingError(error);
		}
	}

	async deleteTimeEntry(taskId: string, entryId: string): Promise<void> {
		try {
			await http.delete(Endpoints.KONVO_CLICKUP_TIME_ENTRY(entryId));
			runInAction(() => {
				const current = this.time.get(taskId);
				if (current) {
					const entries = current.entries.filter((entry) => entry.id !== entryId);
					this.time.set(taskId, {
						...current,
						entries,
						total_ms: entries.reduce((sum, entry) => sum + (entry.duration ?? 0), 0),
					});
				}
				if (this.runningEntry?.id === entryId) this.runningEntry = null;
			});
			void this.loadDetail(taskId, true);
		} catch (error) {
			throw toTimeTrackingError(error);
		}
	}

	/** Fuzzy match against my tasks: exact id / custom id, then name contains. */
	findByQuery(query: string): KonvoTask | null {
		const q = query.trim().toLowerCase();
		if (!q) return null;
		const exact = this.myTasks.find((task) => task.id?.toLowerCase() === q || task.custom_id?.toLowerCase() === q);
		if (exact) return exact;
		const open = this.myTasks.filter((task) => !isTaskClosed(task));
		return (
			open.find((task) => task.name.toLowerCase() === q) ??
			open.find((task) => task.name.toLowerCase().startsWith(q)) ??
			open.find((task) => task.name.toLowerCase().includes(q)) ??
			null
		);
	}
}

export default new KonvoTasksStore();
